package br.com.texeratx.boardgametimeapp.core.spotify.api

import br.com.texeratx.boardgametimeapp.core.spotify.auth.SpotifyAuthManager
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.parameter
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

class SpotifyPlayerApi(
    private val http: HttpClient,
    private val auth: SpotifyAuthManager
) {
    suspend fun getDevices(): Result<List<SpotifyDeviceDto>> = runCatching {
        val token = auth.getValidAccessTokenOrNull()
            ?: error("Spotify não conectado (sem token)")

        val resp: SpotifyDevicesResponse =
            http.get("https://api.spotify.com/v1/me/player/devices") {
                headers.append(HttpHeaders.Authorization, "Bearer $token")
            }.body()

        resp.devices
    }

    suspend fun play(spotifyUrlOrUri: String, deviceId: String): Result<Unit> = runCatching {
        val uri = normalizeToSpotifyUri(spotifyUrlOrUri)

        val token = auth.getAccessTokenOrNull()
            ?: error("Spotify não conectado (sem token)")

        val body: Any = when {
            uri.startsWith("spotify:track:") || uri.startsWith("spotify:episode:") -> {
                PlayBody(uris = listOf(uri))
            }
            else -> {
                PlayBody(contextUri = uri)
            }
        }

        http.put("https://api.spotify.com/v1/me/player/play") {
            parameter("device_id", deviceId)
            header(HttpHeaders.Authorization, "Bearer $token")
            contentType(ContentType.Application.Json)
            setBody(body)
        }
    }

    @Serializable
    private data class PlayBody(
        val uris: List<String>? = null,
        @SerialName("context_uri") val contextUri: String? = null
    )

    // Aceita "spotify:track:..." OU link "https://open.spotify.com/track/ID"
    private fun normalizeToSpotifyUri(input: String): String {
        val trimmed = input.trim()
        if (trimmed.startsWith("spotify:")) return trimmed

        // open.spotify.com/<type>/<id>
        val regex = Regex("""open\.spotify\.com/(track|playlist|album|artist|episode)/([A-Za-z0-9]+)""")
        val match = regex.find(trimmed) ?: return trimmed // fallback (se já vier num formato diferente)
        val type = match.groupValues[1]
        val id = match.groupValues[2]
        return "spotify:$type:$id"
    }

    /**
     * Transfere o "player" para um device específico.
     * Normalmente você chama isso antes de dar play.
     */
    suspend fun transferPlayback(deviceId: String, play: Boolean = false): Result<Unit> = runCatching {
        val token = auth.getValidAccessTokenOrNull()
            ?: error("Spotify não conectado (sem token)")

        http.put("https://api.spotify.com/v1/me/player") {
            headers.append(HttpHeaders.Authorization, "Bearer $token")
            contentType(ContentType.Application.Json)
            setBody(TransferBody(deviceIds = listOf(deviceId), play = play))
        }
    }

    @Serializable
    private data class TransferBody(
        @SerialName("device_ids")
        val deviceIds: List<String>,
        val play: Boolean
    )

}
